import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { withRouter } from "react-router";
import { Button, Divider, List, Loader, Modal, Panel, Placeholder, SelectPicker, Stack, Tag, Tooltip, Uploader, Whisper } from "rsuite";
import { AuthContext } from "../../components/authProvider/main";
import { FileImport, LogImport } from "../../core/types/GenericImportTypes";
import { ImportMethodsUtils } from "../../core/utils/ImportMethodsUtils";
import axios from "../../utilities/axios";
import axiosNoTimeout from "../../utilities/axiosNoTimeout";
import { notify } from "../../utilities/notify";
import LogsModal from "./components/LogsModal";
import ProgressModal from "./components/ProgressModal";
import ResultModal from "./components/ResultModal";
import styles from "./styles/importPage.module.scss";
import ImportErrorsModal, { ImportErrors } from "./components/ImportErrorsModal";
import SocketProgressModal from "./components/SocketProgressModal";
import NoteModal from "./components/NoteModal";

function ImportPage() {
    const { token } = useContext(AuthContext);
    const abortRef = useRef<AbortController | null>(null);

    const [selectedFile, setSelectedFile] = useState<FileImport | null>(null);
    const [history, setHistory] = useState<LogImport[]>([]);
    const [hasMoreHistory, setHasMoreHistory] = useState<boolean>(true);
    const [historyPage, setHistoryPage] = useState(1);
    const [loadingMoreHistory, setLoadingMoreHistory] = useState<boolean>(false);
    const historyListRef = useRef<HTMLDivElement>(null);
    const [uploaderKey, setUploaderKey] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const [integrationsData, setIntegrationsData] = useState<{ value: number, label: string }[]>([]);
    const [isConfirming, setIsConfirming] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [noteOpen, setNoteOpen] = useState(false);

    // Modal de progresso
    const [progressOpen, setProgressOpen] = useState(false);
    const [progress, setProgress] = useState(0);
    const [progressMsg, setProgressMsg] = useState("Enviando arquivo...");
    const [socketModalOpen, setSocketModalOpen] = useState(false);

    // Modal de resultado
    const [resultOpen, setResultOpen] = useState(false);
    const [result, setResult] = useState<any | null>(null);
    const [selectedIntegration, setSelectedIntegration] = useState<number>(0);

    //logs
    const [logOpen, setLogOpen] = useState(false);
    const [logData, setLogData] = useState<any | null>(null);
    const [historyLoadingId, setHistoryLoadingId] = useState<string | number | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmId, setConfirmId] = useState<number | null>(null);
    const [confirmLoading, setConfirmLoading] = useState(false);

    //Modal de Erros de Importação
    const [errorsOpen, setErrorsOpen] = useState(false);
    const [importErrors, setImportErrors] = useState<ImportErrors | null>(null);

    const fileInfo = useMemo(() => {
        if (!selectedFile) return null;
        return {
            name: selectedFile.filename,
            size: selectedFile.size,
            type: selectedFile.extension || "desconhecido",
        };
    }, [selectedFile]);

    useEffect(() => {
        try {
            onLoadScreen();
        } catch (err) {
            console.log("Erro ao carregar histórico de importações.", err);
            notify("Erro ao carregar histórico de importações.", "error");
        }
    }, [])

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (scrollTop + clientHeight >= scrollHeight * 0.9 && hasMoreHistory && !loadingMoreHistory) {
            const nextPage = historyPage + 1;
            setHistoryPage(nextPage);
            fetchImportLog(nextPage, true);
        }
    };

    const resetAndFetchHistory = async () => {
        setHistoryPage(1);
        setHasMoreHistory(true);
        await fetchImportLog(1, false);
    };

    const onLoadScreen = async () => {
        try {
            setLoading(true);
            await resetAndFetchHistory();
            await fetchIntegrations();
        } catch (error) {
            console.log("Erro ao carregar tela.", error);
            notify("Erro ao carregar tela.", "error");
        } finally {
            setLoading(false)
        }
    }

    const fetchIntegrations = async () => {
        try {
            const { data } = await axios.get("/api/v1/integration/getAllActives");
            if (data.status === 200) {
                const integrations = data.data;

                const formattedIntegrations = integrations.map((i: any) => {
                    return {
                        value: i.integration_id,
                        label: `${i.integration_name} - ${i.integration_company_cnpj}`
                    }
                })

                setIntegrationsData(formattedIntegrations);
            }
        } catch (err) {
            console.log("Erro ao carregar integrações.", err);
            notify("Erro ao carregar integrações.", "error");
        }
    }

    const sendToBackend = async (
        fileData: FileImport,
        selectedIntegration: number,
        onProgress: (p: number, phase: "upload" | "process") => void
    ) => {
        onProgress(10, "upload");

        const controller = new AbortController();
        const signal = controller.signal;

        const { data } = await axios.post("/api/v1/import/startImport", { fileData, selectedIntegration, signal });

        console.log("sendToBackend :::>> ", data)

        if (data.status !== 200) {
            notify(data.message, "error");
        }

        onProgress(100, "process");
        return (await data) as any;
    };

    const startImport = async () => {
        if (!selectedFile) {
            notify("Selecione um arquivo antes de iniciar a importação.", "warning");
            return;
        }

        try {
            setIsValidating(true);
            setResult(null);
            setProgress(0);
            setProgressMsg("Enviando arquivo...");
            setProgressOpen(true);

            const data = await sendToBackend(selectedFile, selectedIntegration, (p, phase) => {
                setProgress(p);
                setProgressMsg(phase === "upload" ? "Enviando arquivo..." : "Validando registros...");
            });

            console.log("data :::>>", data);

            setResult(data.data);
            setProgressOpen(false);
            setResultOpen(true);
            notify("Validação concluída.", "success");
        } catch (err: any) {
            if (err) {
                notify("Importação cancelada pelo usuário.", "warning");
            } else {
                const msg = err?.response?.data?.message ?? err?.message ?? "Erro ao importar";
                notify(msg, "error");
            }
            setProgressOpen(false);
        } finally {
            setIsValidating(false);
            abortRef.current = null;
        }
    }

    const confirmImport = async () => {
        if (!selectedFile) return;
        if (!selectedIntegration) {
            notify("Selecione uma integração antes de confirmar a importação.", "warning");
            return;
        }

        try {
            setIsConfirming(true);
            setSocketModalOpen(true);
            setResultOpen(false);
            setLoading(true)

            const { data } = await axiosNoTimeout.post("/api/v1/import/confirmImport", { fileData: selectedFile, integrationId: selectedIntegration });

            const payload = data?.data ?? data;

            const errors = payload?.saveOmieResponse?.errors as ImportErrors | undefined;
            const numClientErr = errors?.clients?.length ?? 0;
            const numAccountErr = errors?.accounts?.length ?? 0;
            const totalErr = numClientErr + numAccountErr;

            if (totalErr > 0) {
                setImportErrors(errors ?? { clients: [], accounts: [] });
                setErrorsOpen(true);

                notify(`Importação concluída com ${totalErr} erro(s).`, "warning");
            } else {
                notify("Importação concluída sem erros.", "success");
            }

            if (data.status === 200) {
                setSelectedFile(null)
                await resetAndFetchHistory()
            }

        } catch (err: any) {
            const msg = err?.response?.data?.message ?? err?.message ?? "Erro ao confirmar importação";
            notify(msg, "error");
            // setProgressOpen(false);
        } finally {
            setIsConfirming(false);
            setLoading(false)
            setSelectedIntegration(0)
            abortRef.current = null;
        }
    };

    const fetchImportLog = async (page: number = 1, append: boolean = false) => {
        try {
            if (!append) {
                setHistory([]);
                setProgressOpen(false);
                setLoading(true);
            } else {
                setLoadingMoreHistory(true);
            }

            const { data } = await axios.get("/api/v1/import/summarized/logs", {
                params: {
                    page,
                    limit: 10
                }
            });

            if (data.status === 200) {
                const responseData = data.data;
                const logs = responseData.data ?? [];
                const pagination = responseData.pagination;

                if (append) {
                    setHistory(prev => [...prev, ...logs]);
                } else {
                    setHistory(logs);
                }

                setHasMoreHistory(pagination?.hasMore ?? false);
            }
        } catch (error: any) {
            console.log("Erro ao obter logs :::", error)
            notify("Erro ao obter logs", "error");
        } finally {
            setLoading(false);
            setLoadingMoreHistory(false);
        }
    }

    const handleHistoryClick = async (item: LogImport) => {
        if (historyLoadingId !== null) return;

        try {
            setHistoryLoadingId(item.id);

            const { data } = await axios.get("/api/v1/import/logs", {
                params: { import_id: item.id }
            });

            setLogData({
                ...data?.data?.dataJSON,
                integrationId: data?.data?.integrationId
            });
            setLogOpen(true);
        } catch (error) {
            console.log("Erro ao obter logs :::", error);
            notify("Erro ao obter logs", "error");
        } finally {
            setHistoryLoadingId(null);
        }
    };

    const openConfirmRemove = (id: number) => {
        setConfirmId(id);
        setConfirmOpen(true);
    };

    const closeConfirm = () => {
        if (!confirmLoading) {
            setConfirmOpen(false);
            setConfirmId(null);
        }
    };

    const handleRemoveHistory = async (id: number) => {
        try {
            setConfirmLoading(true);
            const { data } = await axios.post("/api/v1/import/logs/delete", { logId: id });

            if (data.status === 200) {
                notify("Log removido com sucesso.", "success");
                resetAndFetchHistory();
            } else {
                notify("Não foi possível remover o log.", "warning");
            }
        } catch (error) {
            console.log("Erro ao remover log :::", error);
            notify("Erro ao remover log", "error");
        } finally {
            setConfirmLoading(false);
            closeConfirm();
        }
    };

    const handleShowNote = () => {
        setNoteOpen(!noteOpen)
    }

    return (
        <>
            <div className={styles.container}>
                <div className={styles.main}>
                    <div className={styles.importHeader}>
                        <div className={styles.titleGroup}>
                            <h2 className={styles.pageTitle}>Importação</h2>
                            <p className={styles.pageSubtitle}>
                                Arraste o arquivo aqui ou clique para selecionar (.csv, .xlsx).
                            </p>
                        </div>

                        <Button
                            type="button"
                            color="orange"
                            onClick={handleShowNote}
                        >
                            Nota Orientativa
                        </Button>
                    </div>

                    <div className={styles.integrationSection}>
                        <h4 className={styles.sectionTitle}>Empresa Omie</h4>
                        <SelectPicker
                            data={integrationsData}
                            value={selectedIntegration}
                            onChange={(value) => setSelectedIntegration(value as number)}
                            placeholder="Selecione a empresa para realizar a importação"
                        />
                    </div>

                    <Panel bordered className={styles.dropPanel} style={{ position: "relative" }}>
                        <Uploader
                            key={uploaderKey}
                            className={styles.uploader}
                            loading={loading}
                            draggable
                            multiple={false}
                            autoUpload={true}
                            fileListVisible={false}
                            accept=".csv,.xlsx,.xls"
                            action="/api/v1/import/upload"
                            onChange={() => setLoading(true)}
                            shouldUpload={(file) => {
                                const { name } = file
                                if (name === undefined) return false
                                setLoading(true)

                                if (
                                    !name?.endsWith(".csv") &&
                                    !name?.endsWith(".xlsx") &&
                                    !name?.endsWith(".xls")
                                ) {
                                    notify(
                                        "Formato de arquivo inválido. Apenas CSV, XLSX e XLS são aceitos.",
                                        "error"
                                    )
                                    file.name = undefined
                                    setLoading(false)
                                    return false
                                }

                                return true
                            }}
                            disabled={!!selectedFile || isValidating || isConfirming || !selectedIntegration}
                            onSuccess={(res) => {
                                const { result }: { result: FileImport } = res
                                setSelectedFile(result ?? null)
                                setLoading(false)
                            }}
                            headers={{
                                Authorization: "bearer " + token,
                            }}
                        >
                            <div className={styles.dropArea}>
                                <div className={styles.dropText}>
                                    <strong>Arraste e solte</strong> seu arquivo aqui
                                    <span className={styles.muted}> ou clique para selecionar </span>
                                    <div className={styles.hint}>
                                        Formatos suportados: CSV, XLSX (tamanho máx. ~10MB).
                                    </div>
                                </div>
                            </div>
                        </Uploader>

                        {fileInfo ? (
                            <>
                                <Divider className={styles.divider} />
                                <Stack justifyContent="space-between" alignItems="center" spacing={4}>
                                    <Stack style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: ".5rem", maxWidth: "400px" }}>
                                        <div>
                                            <div className={styles.fileName}>{fileInfo.name}</div>
                                            <div className={styles.fileMeta}>
                                                <span>{ImportMethodsUtils.kb(fileInfo.size)}</span>
                                                <span className={styles.metaDot}>•</span>
                                                <span>{fileInfo.type}</span>
                                            </div>
                                        </div>
                                        <Divider vertical style={{ height: "1.8rem" }} />
                                        <Whisper
                                            placement="top"
                                            trigger="hover"
                                            speaker={<Tooltip>Remover arquivo selecionado</Tooltip>}
                                        >
                                            <Button
                                                color="red"
                                                size="sm"
                                                onClick={() => {
                                                    setSelectedFile(null)
                                                    setUploaderKey(k => k + 1);
                                                }}
                                                disabled={isValidating || isConfirming}
                                            >
                                                <i className="fas fa-trash"></i>
                                            </Button>
                                        </Whisper>
                                    </Stack>
                                    <Stack spacing={8} alignItems="flex-end">
                                        <Tag color="violet" className={styles.pulseTag}>Pronto para importar</Tag>
                                        <Button
                                            appearance="primary"
                                            onClick={startImport}
                                            disabled={!selectedFile || isValidating || isConfirming || !selectedIntegration}
                                            color="blue"
                                            loading={loading}
                                            size="sm"
                                        >
                                            {isValidating ? "Validando..." : "Iniciar Importação (pré-validação)"}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </>
                        ) : (
                            <>
                                <Divider className={styles.divider} />
                                <Placeholder.Paragraph rows={1} graph="image" />
                            </>
                        )}

                        {/* Overlay Loader */}
                        {loading && (
                            <Loader
                                backdrop
                                center
                                vertical
                                content="Carregando dados..."
                            />
                        )}
                    </Panel>

                    <Panel bordered header="Importações anteriores" className={styles.historyPanel}>
                        {history.length === 0 && !loading ? (
                            <Placeholder.Paragraph rows={2} />
                        ) : (
                            <div
                                ref={historyListRef}
                                style={{ maxHeight: 350, overflowY: "auto" }}
                                onScroll={handleScroll}
                            >
                                <List
                                    bordered
                                    className={styles.historyList}
                                >
                                    {history.map((item) => {
                                        const isLoading = historyLoadingId === item.id;

                                        return (
                                            <List.Item
                                                key={item.id}
                                                className={styles.historyItem}
                                                onClick={() => (!isLoading ? handleHistoryClick(item) : null)}
                                                style={{
                                                    opacity: isLoading ? 0.6 : 1,
                                                    cursor: isLoading ? "not-allowed" : "pointer",
                                                    pointerEvents: isLoading ? "none" : "auto"
                                                }}
                                            >
                                                <Stack alignItems="center" justifyContent="space-between">
                                                    <Stack spacing={10}>
                                                        <div className={styles.historyFileBlock}>
                                                            <div className={styles.historyFile}>{item.filename}</div>
                                                            <div className={styles.historyMeta}>
                                                                {new Date(item.createdAt).toLocaleString()}
                                                            </div>
                                                        </div>
                                                        <div className={styles.counts}>
                                                            <Tag color="green">✓ {item.oks}</Tag>
                                                            <Tag color="red">⨯ {item.errors}</Tag>
                                                            <Tag>{item.total} total</Tag>
                                                        </div>
                                                    </Stack>

                                                    {isLoading ? (
                                                        <Loader size="sm" content="Detalhes..." />
                                                    ) : (
                                                        <div
                                                            className={styles.logActions}
                                                        >
                                                            <Tag
                                                                color={ImportMethodsUtils.statusToColor("success")}
                                                                className={styles.statusTag}
                                                            >
                                                                Concluído
                                                            </Tag>

                                                            <Whisper placement="top" trigger="hover" speaker={<Tooltip>Remover do Histórico</Tooltip>}>
                                                                <Button
                                                                    color="red"
                                                                    size="xs"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        openConfirmRemove(item.id);
                                                                    }}
                                                                    disabled={isValidating || isConfirming}
                                                                    className={styles.removeButton}
                                                                >
                                                                    <i className="fas fa-trash"></i>
                                                                </Button>
                                                            </Whisper>
                                                        </div>
                                                    )}
                                                </Stack>
                                            </List.Item>
                                        );
                                    })}

                                    {loadingMoreHistory && (
                                        <div style={{ padding: '16px', textAlign: 'center' }}>
                                            <Loader size="sm" content="Carregando mais registros..." />
                                        </div>
                                    )}

                                    {!hasMoreHistory && history.length > 0 && (
                                        <div style={{ padding: '16px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                                            Não há mais registros para exibir
                                        </div>
                                    )}
                                </List>
                            </div>
                        )}
                    </Panel>
                </div>
            </div>

            <ProgressModal
                open={progressOpen}
                progress={progress}
                progressMsg={progressMsg}
                onCancel={() => { }}
            />

            <ResultModal
                open={resultOpen}
                result={result}
                onClose={() => setResultOpen(false)}
                onConfirm={confirmImport}
                isConfirming={isConfirming}
                integrationsSelect={integrationsData}
                selectedIntegration={selectedIntegration}
                onSelectIntegration={setSelectedIntegration}
            />

            <LogsModal
                open={logOpen}
                data={logData}
                onClose={() => setLogOpen(false)}
                integrationsSelect={integrationsData}
            />

            <ImportErrorsModal
                open={errorsOpen}
                errors={importErrors}
                onClose={() => setErrorsOpen(false)}
            />

            <SocketProgressModal
                open={socketModalOpen}
                onClose={() => setSocketModalOpen(false)}
                event="import:progress"
                doneEvent="import:done"
                errorEvent="import:error"
                title="Importando dados..."
                autoCloseOn100
            />

            <NoteModal open={noteOpen} setOpen={setNoteOpen} />

            <Modal open={confirmOpen} onClose={closeConfirm} size="xs">
                <Modal.Header>
                    <Modal.Title>Remover do histórico</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Tem certeza que deseja remover este log?
                    <br />
                    <small>Esta ação não pode ser desfeita.</small>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        appearance="primary"
                        color="red"
                        onClick={() => confirmId != null && handleRemoveHistory(confirmId)}
                        loading={confirmLoading}
                        disabled={confirmId == null}
                    >
                        Remover
                    </Button>
                    <Button appearance="subtle" onClick={closeConfirm} disabled={confirmLoading}>
                        Cancelar
                    </Button>
                </Modal.Footer>
            </Modal>

        </>
    );
}

export default withRouter(ImportPage);
